import { Box, Stack } from "@mui/material";
import parlors from "goi_common/src/constants/parlors_detail.json";

import {
  ParlorRegionHeadSection,
  DTypeCardView,
  DTypeRegionHead,
  DTypeParlorList,
  FUNERAL_SIDO,
  FUNERAL_SIGUNGU,
  ParlorDetailProps,
  numberToLocalString,
  GOI_WEB_URL,
  ATypeBreadCrumb,
  ATypeCTA,
  buildParlorRegionPageSchema,
  ParlorRegionFAQ,
  RichSearch,
} from "goi_common";
import { parlorRegion } from "@/constants/parlorRegion";
import { GetStaticPaths, GetStaticProps } from "next";
import Head from "next/head";

// Path 로직 안내
// FUNERAL_SIDO, SIGUNGU의 key로 path 설정.
//
// 1. getStaticPaths 내 key,value 받아서 value(key)로 추가한 뒤 params로 넘기게 변경
// 2. getStaticProps 내 props sido,sigungu, sidoName, sigunguName에 split,replace 로직 추가

const { regionColor, targetSidos } = parlorRegion;

export const getStaticPaths: GetStaticPaths = async () => {
  const sidoList = Array.from(FUNERAL_SIDO).filter(([key, val]) =>
    targetSidos.includes(String(key))
  );

  const sidoPath = sidoList.map(([key, val]) => {
    return {
      params: {
        code: `장례식장 리스트 (${val})`,
      },
    };
  });

  const sigunguPaths = Array.from(FUNERAL_SIDO)
    .filter(([key, val]) => targetSidos.includes(String(key)))
    .map(([sidoKey, sidoVal]) => {
      const sigungus = FUNERAL_SIGUNGU.get(sidoKey);
      if (sigungus) {
        return Array.from(sigungus).map(([sigunguKey, sigunguVal]) => {
          return {
            params: {
              code: `장례식장 리스트 (${sidoVal}-${sigunguVal})`,
            },
          };
        });
      }
    })
    .filter((i) => i != null);

  return {
    paths: sidoPath.concat(sigunguPaths?.flat() as Array<any>),
    fallback: false,
  };
};

function getByValue(map: Map<number, any>, searchValue: any) {
  for (const [key, value] of map.entries()) {
    if (value === searchValue) return key;
  }
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const code = params?.code as string;

  if (code.includes("-")) {
    const [sido, sigungu] = code.split("-");
    const sidoName = sido.replace("장례식장 리스트 (", "");
    const sidoNumber = getByValue(FUNERAL_SIDO, sidoName);
    const sigunguName = sigungu.replace(")", "");
    const sigunguNumber = getByValue(
      FUNERAL_SIGUNGU.get(sidoNumber as number) as any,
      sigunguName
    );

    const regionParlors = parlors.filter(
      (p) => String(p.sigungu) === String(sigunguNumber)
    );

    const 가족장 = Number(
      (
        regionParlors.reduce((prev, curr) => {
          return curr.expected_charge_sum["가족장"] + prev;
        }, 0) / regionParlors.length
      ).toFixed(0)
    ).toLocaleString();
    const 무빈소장 = Number(
      (
        regionParlors.reduce((prev, curr) => {
          return curr.expected_charge_sum["무빈소"] + prev;
        }, 0) / regionParlors.length
      ).toFixed(0)
    ).toLocaleString();
    const 일반장 = Number(
      (
        regionParlors.reduce((prev, curr) => {
          return curr.expected_charge_sum["일반장"] + prev;
        }, 0) / regionParlors.length
      ).toFixed(0)
    ).toLocaleString();

    let parlorsString = "";
    regionParlors.map((p: any) => {
      if (p.binsos.length > 0) {
        parlorsString += `${p.companyname} ${numberToLocalString(
          p.binsos[0].charge
        )}원, `;
      }
    });

    const description = `${sidoName} ${sigunguName} 내 ${regionParlors.length}곳의 장례식장을 대상으로 한 비용 비교: 광주, 전남, 전북 내 모든 장례식장에 대한 비용을 알아보세요. 근조화환, 실시간 빈소, 장례식장 위치 등 해당 장례식장에 필요한 정보들을 알아보세요.`;

    return {
      props: {
        regionParlors,
        expectedParlorPrice: { 무빈소장, 가족장, 일반장 },
        sidoName,
        sido: sidoNumber,
        sigunguName,
        sigungu: sigunguNumber,
        isSigungu: true,
        pageDescription: description,
        pageKeywords: `${sidoName} ${sigunguName} 장례식장, ${sidoName} ${sigunguName} 장례식장 추천, ${sidoName} ${sigunguName} 장례식장 비용, ${sidoName} ${sigunguName} 장례식장 평균 예상 비용, ${sidoName} ${sigunguName}  무빈소, ${sidoName} ${sigunguName}  가족장, ${sidoName} ${sigunguName} `,
        pageOgTitle: `${sidoName} ${sigunguName} 지역의 장례식장 목록, 추천, 안내 :: 전라광주장례추천포털`,
        pageOgDescription: description,
        pageOgImage:
          regionParlors.length > 0
            ? regionParlors[0]?.images[0]?.url || ""
            : "",
      },
    };
  } else {
    const sidoName = code.replace("장례식장 리스트 (", "").replace(")", "");
    const sidoNumber = getByValue(FUNERAL_SIDO, sidoName);
    const regionParlors = parlors.filter((p) => p.sido === String(sidoNumber));

    const 가족장 = Number(
      (
        regionParlors.reduce((prev, curr) => {
          return curr.expected_charge_sum["가족장"] + prev;
        }, 0) / regionParlors.length
      ).toFixed(0)
    ).toLocaleString();
    const 무빈소장 = Number(
      (
        regionParlors.reduce((prev, curr) => {
          return curr.expected_charge_sum["무빈소"] + prev;
        }, 0) / regionParlors.length
      ).toFixed(0)
    ).toLocaleString();
    const 일반장 = Number(
      (
        regionParlors.reduce((prev, curr) => {
          return curr.expected_charge_sum["일반장"] + prev;
        }, 0) / regionParlors.length
      ).toFixed(0)
    ).toLocaleString();

    let parlorsString = "";
    regionParlors.map((p: any) => {
      if (p.binsos.length > 0) {
        parlorsString += `${p.companyname} ${numberToLocalString(
          p.binsos[0].charge
        )}원, `;
      }
    });

    const description = `${sidoName} 내 ${regionParlors.length}곳의 장례식장을 대상으로 한 비용 비교: 광주, 전남, 전북 내 모든 장례식장에 대한 비용을 알아보세요. 근조화환, 실시간 빈소, 장례식장 위치 등 해당 장례식장에 필요한 정보들을 알아보세요.`;

    return {
      props: {
        regionParlors,
        expectedParlorPrice: { 무빈소장, 가족장, 일반장 },
        sidoName,
        sido: sidoNumber,
        isSigungu: false,
        pageDescription: description,
        pageKeywords: `${sidoName} 장례식장, ${sidoName} 장례식장 추천, ${sidoName} 장례식장 비용, ${sidoName} 장례식장 평균 예상 비용, ${sidoName} 무빈소, ${sidoName} 가족장, ${sidoName} `,
        pageOgTitle: `${sidoName} 지역의 장례식장 목록, 추천, 안내 :: 전라광주장례추천포털`,
        pageOgDescription: description,
        pageOgImage:
          regionParlors.length > 0
            ? regionParlors[0]?.images[0]?.url || ""
            : "",
      },
    };
  }
};

export default function ParlorSlugTypePage({
  regionParlors,
  expectedParlorPrice,
  pageOgTitle,
  sido,
  sidoName,
  sigungu,
  sigunguName,
  isSigungu,
}: {
  regionParlors: Array<ParlorDetailProps>;
  expectedParlorPrice: { 무빈소장: number; 가족장: number; 일반장: number };
  pageOgTitle: string;
  sido: string;
  sidoName: string;
  sigungu?: string;
  sigunguName?: string;
  isSigungu: boolean;
}) {
  if (regionParlors.length === 0) {
    return <></>;
  }

  const parlorRegionPageSchema = buildParlorRegionPageSchema(regionParlors, {
    regionText: `${sidoName}${isSigungu && sigunguName ? ` ${sigunguName}` : ""}`,
    itemUrlBuilder: (id) =>
      `https://www.honam-funeralcenter.co.kr/funeral-recommend-items/${id}/`,
  });

  return (
    <>
      <Head>
        <title>{pageOgTitle}</title>
      </Head>
      <RichSearch data={parlorRegionPageSchema} />

      <ParlorRegionHeadSection
        breadCrumb={
          <ATypeBreadCrumb
            firstItem={{
              text: `장례식장 추천`,
              href: `${GOI_WEB_URL}?utm_source=dogsound` + "/match/request/?utm_source=dogsound",
              event: {
                domain: "honam",
                path: "prlr_recm",
                trigger_and_target: "br_crumb_mth_req",
              },
            }}
            secondItem={{
              text: `${sidoName} 장례식장`,
              href: `/region-list-summary/장례식장 리스트 (${sidoName})/`,
              event: {
                domain: "honam",
                path: "prlr_recm",
                trigger_and_target: "br_crumb_sido_prlr_list",
              },
            }}
            thirdItem={
              isSigungu
                ? {
                    text: `${sigunguName} 장례식장`,
                    href: `/region-list-summary/장례식장 리스트 (${sidoName}-${sigunguName})/`,
                    event: {
                      domain: "honam",
                      path: "prlr_recm",
                      trigger_and_target: "br_crumb_sigungu_prlr_list",
                    },
                  }
                : undefined
            }
            goiHomeEvent={{
              domain: "honam",
              path: "prlr_recm",
              trigger_and_target: "br_crumb_goi_home",
            }}
          />
        }
        title={
          <DTypeRegionHead
            regionParlors={regionParlors}
            sidoName={sidoName}
            sigunguName={sigunguName}
            expectedParlorPrice={expectedParlorPrice}
            mainColor={regionColor}
          />
        }
        ctaList={[]}
      />

      <Stack padding="20px">
        <ATypeCTA
          sidoName={sidoName}
          sigunguName={sigunguName}
          mainColor={regionColor}
          event={{
            domain: "honam",
            path: "prlr_recm",
            trigger_and_target: "CTA_uv_mth_req",
          }}
        />
      </Stack>
      <Stack padding="20px">
        <Box width="100%" height="1px" style={{ background: "black" }} />
      </Stack>

      <Stack padding="20px">
        <DTypeParlorList
          sido={sido}
          sigungu={sigungu}
          title={`${sidoName}${
            sigunguName ? " " + sigunguName : ""
          }의 다른 장례식장`}
          binsoAveragePrice={true}
          CardViewType={(res) => (
            <DTypeCardView
              address={res.address}
              descriptions={[
                `평균 ${Number(res.binso_average_charge).toLocaleString()}원`,
              ]}
              key={res.companyname}
              name={res.companyname}
              href={`/funeral-recommend-items/${res.id}/`}
              imgSrc={res.images[0] ? res.images[0].url : ""}
              event={{
                domain: "honam",
                path: "prlr_recm",
                trigger_and_target: "uv_prlr_detail",
              }}
            />
          )}
        />
        <Box padding="20px 0 " />
        {isSigungu && (
          <DTypeParlorList
            sido={sido}
            title={`${sidoName}의 다른 장례식장`}
            binsoAveragePrice={true}
            CardViewType={(res) => (
              <DTypeCardView
                address={res.address}
                descriptions={[
                  `평균 ${Number(res.binso_average_charge).toLocaleString()}원`,
                ]}
                key={res.companyname}
                name={res.companyname}
                href={`/funeral-recommend-items/${res.id}/`}
                imgSrc={res.images[0] ? res.images[0].url : ""}
                event={{
                  domain: "honam",
                  path: "prlr_recm",
                  trigger_and_target: "uv_sigun_prlr_detail",
                }}
              />
            )}
          />
        )}
      </Stack>

      <ParlorRegionFAQ />
    </>
  );
}
